import { createClient } from "@connectrpc/connect";
import { SYNC_KEY_TERCEROS_CLIENTES } from "../../../constantes";
import {
  ClientePlataformaItem,
  ClientesPlataformaService,
  FincaResumenPlataformaItem,
  DispositivoPlataformaItem,
  UsuarioAppPlataformaItem,
} from "../../../shared/generated/grpc/plataforma_terceros_clientes_pb";
import { UsuarioAppResponse } from "../../../shared/response/seguridad/UsuarioAppResponse";
import { ClienteDetalleResponse } from "../../../shared/response/terceros/ClienteDetalleResponse";
import { ClienteDispositivoResponse } from "../../../shared/response/terceros/ClienteDispositivoResponse";
import { ClienteResponse } from "../../../shared/response/terceros/ClienteResponse";
import { FincaResumenResponse } from "../../../shared/response/terceros/FincaResumenResponse";
import { ServiceGetListBase } from "../../../shared/services/base/ServiceGetListBase";
import { grpcTransportProvider } from "../../../shared/services/grpc/GrpcTransport";
import { mapConnectError } from "../../../shared/services/grpc/mapConnectError";
import { coincideBusqueda, palabrasBusqueda } from "../../../shared/utils/busquedaTexto";

/**
 * Clientes del menú Clientes: tabla (lista en memoria) y detalle, ambos por gRPC-Web. La misma lista alimenta todos los
 * buscadores de clientes de la Plataforma (`buscarClientes`, en el navegador).
 */
export class ClienteService extends ServiceGetListBase<ClienteResponse> {
  static readonly instance = new ClienteService();
  private constructor() { super(); }

  /** Clave única del listado: deduplica `sync()` (dos pedidos simultáneos, un solo request). */
  protected readonly syncKey = SYNC_KEY_TERCEROS_CLIENTES;
  protected readonly sortBy = "nombre";

  /** gRPC-Web (`ClientesPlataformaService.ListClientes`); el servidor ya excluye los eliminados. */
  protected async fetchItems(): Promise<ClienteResponse[]> {
    const client = createClient(ClientesPlataformaService, grpcTransportProvider.get());
    try {
      const response = await client.listClientes({});
      return response.items.map(toClienteResponse);
    } catch (error) {
      throw mapConnectError(error);
    }
  }

  /**
   * Cliente + fincas (resumen) + cuentas de la app + dispositivos en una sola llamada
   * (`ClientesPlataformaService.GetClienteDetalle`). Lanza `NotFoundError` si el cliente
   * no existe o está eliminado.
   */
  async getDetalle(id: string): Promise<ClienteDetalleResponse> {
    const client = createClient(ClientesPlataformaService, grpcTransportProvider.get());
    try {
      const response = await client.getClienteDetalle({ id });
      if (!response.cliente) throw new Error("Respuesta sin cliente");
      return {
        cliente: toClienteResponse(response.cliente),
        fincas: response.fincas.map(toFincaResumenResponse),
        usuariosApp: response.usuariosApp.map(toUsuarioAppResponse),
        dispositivos: response.dispositivos.map(toClienteDispositivoResponse),
      };
    } catch (error) {
      throw mapConnectError(error);
    }
  }
}

/**
 * Buscador de clientes de toda la Plataforma (2026-10-28): los de `clientes` cuyo nombre o NIT tienen todas las palabras
 * de `texto`, sin distinguir mayúsculas ni tildes (`busquedaTexto`), ordenados por nombre y hasta `max`. Busca en el
 * navegador sobre la lista de `ListClientes`, que a un promotor ya le llega acotada a su cartera; reemplazó al RPC
 * `BuscarClientes`, que buscaba en el servidor. Texto vacío = ninguno.
 */
export function buscarClientes(clientes: ClienteResponse[], texto: string, max: number): ClienteResponse[] {
  const palabras = palabrasBusqueda(texto);
  if (palabras.length === 0) return [];
  const orden = new Intl.Collator(undefined, { sensitivity: "base" });
  return clientes
    .filter((c) => coincideBusqueda(palabras, c.nombre, c.nit))
    .sort((a, b) => orden.compare(a.nombre, b.nombre))
    .slice(0, max);
}

/** Unix ms (int64 → bigint) a `Date`; `null` si el campo opcional no vino. */
function msToDate(ms: bigint | undefined): Date | null {
  return ms !== undefined ? new Date(Number(ms)) : null;
}

/**
 * Mensaje gRPC del cliente → `ClienteResponse` que consumen las páginas. Lo reusan save y plan. Ubicación, núcleo y
 * proyectos activos solo vienen llenos en `ListClientes`.
 */
export function toClienteResponse(item: ClientePlataformaItem): ClienteResponse {
  return {
    id: item.id,
    isEliminado: false,
    nit: item.nit,
    nombre: item.nombre,
    email: item.email,
    telefono: item.telefono,
    isDemoPlataforma: item.isDemoPlataforma,
    fechaVencimientoPlataforma: msToDate(item.fechaVencimientoPlataforma),
    maximoHectareasPlataforma: item.maximoHectareasPlataforma,
    cantDispositivos: item.cantDispositivos,
    cantFincas: item.cantFincas,
    hectareasTotales: item.hectareasTotales,
    promotorId: item.promotorId ?? null,
    promotorNombre: item.promotorNombre ?? null,
    ubicacion: item.ubicacion ?? null,
    nucleoAsignado: item.nucleoAsignado ?? null,
    proyectosActivos: item.proyectosActivos,
  };
}

/** Finca resumida del detalle → `FincaResumenResponse`; los opcionales ausentes quedan en `null`. */
function toFincaResumenResponse(item: FincaResumenPlataformaItem): FincaResumenResponse {
  return {
    id: item.id,
    isEliminado: false,
    nombreFinca: item.nombreFinca,
    municipioNombre: item.municipioNombre ?? null,
    departamentoNombre: item.departamentoNombre ?? null,
    hectareasFinca: item.hectareasFinca,
    cultivos: item.cultivos,
  };
}

/** Cuenta de la app web del detalle → `UsuarioAppResponse`. */
function toUsuarioAppResponse(item: UsuarioAppPlataformaItem): UsuarioAppResponse {
  return {
    id: item.id,
    isEliminado: false,
    nombre: item.nombre,
    email: item.email,
    isActivo: item.isActivo,
    hasUsuarios: item.hasUsuarios,
  };
}

/** GPS tracker del detalle → `ClienteDispositivoResponse`. */
function toClienteDispositivoResponse(item: DispositivoPlataformaItem): ClienteDispositivoResponse {
  return {
    codigoInterno: item.codigoInterno,
    codigoCliente: item.codigoCliente,
    testeado: item.testeado,
    pasaTest: item.pasaTest,
    notaTest: item.notaTest ?? null,
  };
}
